import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/TailwindComponents';
import {
  AddressStatus,
  IAddress,
  IKaseMasterName,
  IMasterNamePerson,
  KaseMasterNameInvolvementLabels,
} from '@/models';
import { formatPersonName } from '@/utils';

interface AssociatedPersonsTabProps {
  associatedPersons?: IKaseMasterName[];
}

const AssociatedPersonsTab = ({
  associatedPersons = [],
}: AssociatedPersonsTabProps) => {
  // Helper to get address from either addresses array or legacy address field
  const getAddress = (kaseMasterName: IKaseMasterName): IAddress | null => {
    const masterName = kaseMasterName.masterName;
    if (!masterName) return null;

    // Check addresses array first (current pattern)
    if (masterName.addresses && masterName.addresses.length > 0) {
      // Prefer first ACTIVE address, fall back to first address
      const activeAddress = masterName.addresses.find(
        (addr) => addr.status === AddressStatus.ACTIVE,
      );
      return activeAddress || masterName.addresses[0];
    }

    // Fall back to single address field (legacy)
    if (masterName.address) {
      return masterName.address;
    }

    return null;
  };

  const formatName = (kaseMasterName: IKaseMasterName): string => {
    const masterName = kaseMasterName.masterName;
    if (!masterName) return '-';

    // If name property exists, use it directly
    if (masterName.name) return masterName.name;

    // For persons, construct name from firstName and lastName
    if (masterName.kind === 'MasterNamePerson') {
      const person = masterName as IMasterNamePerson;
      const firstName = person.firstName || '';
      const lastName = person.lastName || '';
      const fullName = formatPersonName({ firstName, lastName });
      return fullName || '-';
    }

    // Fallback for organizations or unknown types
    return '-';
  };

  const formatAddress = (kaseMasterName: IKaseMasterName): string => {
    const address = getAddress(kaseMasterName);
    if (!address) return '-';

    const parts = [];
    if (address.addressNumber) parts.push(address.addressNumber);
    if (address.completeStreetName) parts.push(address.completeStreetName);

    return parts.join(' ') || '-';
  };

  const formatCityStateZip = (kaseMasterName: IKaseMasterName): string => {
    const address = getAddress(kaseMasterName);
    if (!address) return '-';

    const parts = [];
    if (address.zipName) parts.push(address.zipName);
    if (address.state) parts.push(address.state);
    if (address.zipcode) parts.push(address.zipcode);

    return parts.join(', ') || '-';
  };

  const formatPhoneNumber = (kaseMasterName: IKaseMasterName): string => {
    const phoneNumbers = kaseMasterName.masterName?.phoneNumbers;
    if (!phoneNumbers || phoneNumbers.length === 0) return '-';

    return phoneNumbers[0].phoneNumber || '-';
  };

  const formatInvolvement = (kaseMasterName: IKaseMasterName): string => {
    const involvement = kaseMasterName.relationship?.involvement || [];
    return (
      involvement
        .map((inv) => KaseMasterNameInvolvementLabels[inv] || inv)
        .join(', ') || '-'
    );
  };

  const columns = [
    { key: 'involvement', label: 'Involvement' },
    { key: 'name', label: 'Name' },
    { key: 'streetNameAndNumber', label: 'Street Name and Number' },
    { key: 'cityStateZip', label: 'City / State / Zip code' },
    { key: 'phoneNumber', label: 'Phone number' },
  ];

  return (
    <div className="space-y-6">
      <div className="overflow-auto">
        <Table>
          <TableHeader className="sticky top-0">
            <TableRow className="py-2">
              {columns.map((column, index) => (
                <TableHead
                  key={column.key}
                  className={index > 0 ? 'border-l border-gray-200' : ''}
                >
                  {column.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {associatedPersons.length > 0 ? (
              associatedPersons.map((kaseMasterName) => (
                <TableRow key={kaseMasterName._id}>
                  <TableCell>{formatInvolvement(kaseMasterName)}</TableCell>
                  <TableCell>{formatName(kaseMasterName)}</TableCell>
                  <TableCell>{formatAddress(kaseMasterName)}</TableCell>
                  <TableCell>{formatCityStateZip(kaseMasterName)}</TableCell>
                  <TableCell>{formatPhoneNumber(kaseMasterName)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-center text-gray-500 py-4"
                >
                  No associated persons found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AssociatedPersonsTab;
